/**
 * ConsultasTab - Admin Queries Management Component
 * Extracted from AdminPage.jsx for better code organization
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { 
  ClipboardList, Plus, Search, Check, Clock, Pencil, Trash2 
} from 'lucide-react';
import { useAdminTranslation } from '../../utils/useAdminTranslation';

const API = process.env.REACT_APP_BACKEND_URL;

export const ConsultasTab = ({ users = [], productEmails = [], onRefresh }) => {
  const { tx } = useAdminTranslation();
  
  // State
  const [consultas, setConsultas] = useState([]);
  const [consultaDialogOpen, setConsultaDialogOpen] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [consultaForm, setConsultaForm] = useState({
    worker_id: '',
    worker_name: '',
    worker_email: '',
    client_id: '',
    client_name: '',
    client_email: '',
    editor_id: '',
    editor_name: '',
    editor_email: '',
    start_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
  });
  const [consultaStatusFilter, setConsultaStatusFilter] = useState('');
  const [consultaSearch, setConsultaSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch consultas
  const fetchConsultas = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/consultas`);
      setConsultas(res.data || []);
    } catch (error) {
      console.error('Error fetching consultas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultas();
  }, []);

  // Filter users by role
  const getClients = () => users.filter(u => !u.is_admin && u.role !== 'Trabajador');
  const getWorkers = () => users.filter(u => u.role === 'Trabajador' || u.is_admin);

  // Handlers
  const handleCreateConsulta = async () => {
    try {
      await axios.post(`${API}/admin/consultas`, consultaForm);
      toast.success(tx('Consulta creada', 'Query created'));
      setConsultaDialogOpen(false);
      resetForm();
      fetchConsultas();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating consulta');
    }
  };

  const handleUpdateConsulta = async (consultaId, updates) => {
    try {
      await axios.put(`${API}/admin/consultas/${consultaId}`, updates);
      toast.success(tx('Consulta actualizada', 'Query updated'));
      fetchConsultas();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating consulta');
    }
  };

  const handleFinalizeConsulta = async (consultaId) => {
    try {
      await axios.post(`${API}/admin/consultas/${consultaId}/finalize`);
      toast.success(tx('Consulta finalizada', 'Query finalized'));
      fetchConsultas();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error finalizing consulta');
    }
  };

  const handleDeleteConsulta = async (consultaId) => {
    if (!window.confirm(tx('¿Eliminar esta consulta?', 'Delete this query?'))) return;
    try {
      await axios.delete(`${API}/admin/consultas/${consultaId}`);
      toast.success(tx('Consulta eliminada', 'Query deleted'));
      fetchConsultas();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting consulta');
    }
  };

  const resetForm = () => {
    setConsultaForm({
      worker_id: '',
      worker_name: '',
      worker_email: '',
      client_id: '',
      client_name: '',
      client_email: '',
      editor_id: '',
      editor_name: '',
      editor_email: '',
      start_date: new Date().toISOString().split('T')[0],
      description: '',
      notes: '',
    });
  };

  const openNewConsultaDialog = () => {
    setEditingConsulta(null);
    resetForm();
    setConsultaDialogOpen(true);
  };

  const openEditConsultaDialog = (consulta) => {
    setEditingConsulta(consulta);
    setConsultaForm({
      worker_id: consulta.worker_id || '',
      worker_name: consulta.worker_name || '',
      worker_email: consulta.worker_email || '',
      client_id: consulta.client_id || '',
      client_name: consulta.client_name || '',
      client_email: consulta.client_email || '',
      editor_id: consulta.editor_id || '',
      editor_name: consulta.editor_name || '',
      editor_email: consulta.editor_email || '',
      start_date: consulta.start_date || '',
      description: consulta.description || '',
      notes: consulta.notes || '',
    });
    setConsultaDialogOpen(true);
  };

  // Filter consultas
  const filteredConsultas = consultas.filter(c => {
    const matchesSearch = !consultaSearch || 
      c.client_name?.toLowerCase().includes(consultaSearch.toLowerCase()) ||
      c.client_email?.toLowerCase().includes(consultaSearch.toLowerCase()) ||
      c.worker_name?.toLowerCase().includes(consultaSearch.toLowerCase()) ||
      c.editor_name?.toLowerCase().includes(consultaSearch.toLowerCase());
    const matchesStatus = !consultaStatusFilter || consultaStatusFilter === 'all' || c.status === consultaStatusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-orange-600" />
                {tx('Gestión de Consultas', 'Query Management')}
              </CardTitle>
              <CardDescription>
                {tx('Administra las consultas de clientes', 'Manage customer queries')}
              </CardDescription>
            </div>
            <Button onClick={openNewConsultaDialog} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="h-4 w-4 mr-2" />
              {tx('Nueva Consulta', 'New Query')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={tx('Buscar por cliente o trabajador...', 'Search by client or worker...')}
                value={consultaSearch}
                onChange={(e) => setConsultaSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={consultaStatusFilter} onValueChange={setConsultaStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={tx('Todos los estados', 'All statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx('Todos', 'All')}</SelectItem>
                <SelectItem value="Ejecutándose">{tx('Ejecutándose', 'In Progress')}</SelectItem>
                <SelectItem value="Finalizada">{tx('Finalizada', 'Completed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Consultas Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">#</TableHead>
                  <TableHead>{tx('Estado', 'Status')}</TableHead>
                  <TableHead>{tx('Cliente', 'Client')}</TableHead>
                  <TableHead>{tx('Editor', 'Editor')}</TableHead>
                  <TableHead>{tx('Trabajador', 'Worker')}</TableHead>
                  <TableHead>{tx('Fecha Inicio', 'Start Date')}</TableHead>
                  <TableHead>{tx('Fecha Fin', 'End Date')}</TableHead>
                  <TableHead>{tx('Descripción', 'Description')}</TableHead>
                  <TableHead className="text-right">{tx('Acciones', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConsultas.map((consulta) => (
                  <TableRow key={consulta.id}>
                    <TableCell className="font-mono text-sm">C#{consulta.consulta_number}</TableCell>
                    <TableCell>
                      <Badge className={consulta.status === 'Finalizada' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                        {consulta.status === 'Finalizada' ? (
                          <><Check className="h-3 w-3 mr-1" /> {tx('Finalizada', 'Completed')}</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" /> {tx('Ejecutándose', 'In Progress')}</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{consulta.client_name || '-'}</span>
                        <span className="text-xs text-muted-foreground">{consulta.client_email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{consulta.editor_name || '-'}</span>
                        <span className="text-xs text-muted-foreground">{consulta.editor_email || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{consulta.worker_name || '-'}</span>
                        <span className="text-xs text-muted-foreground">{consulta.worker_email || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{consulta.start_date || '-'}</TableCell>
                    <TableCell>{consulta.end_date || '-'}</TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="truncate text-sm" title={consulta.description}>
                        {consulta.description || '-'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {consulta.status !== 'Finalizada' && (
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleFinalizeConsulta(consulta.id)}
                            title={tx('Finalizar consulta', 'Finalize query')}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            {tx('Finalizar', 'Finalize')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditConsultaDialog(consulta)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteConsulta(consulta.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {consultas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {tx('No hay consultas registradas', 'No queries registered')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Consulta Dialog */}
      <Dialog open={consultaDialogOpen} onOpenChange={setConsultaDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-orange-600" />
              {editingConsulta 
                ? tx('Editar Consulta', 'Edit Query')
                : tx('Nueva Consulta', 'New Query')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>{tx('Cliente *', 'Client *')}</Label>
              <Select
                value={consultaForm.client_email}
                onValueChange={(email) => {
                  const client = getClients().find(c => c.email === email);
                  setConsultaForm({
                    ...consultaForm,
                    client_id: client?.id || '',
                    client_name: client?.name || '',
                    client_email: email,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={tx('Seleccionar cliente...', 'Select client...')} />
                </SelectTrigger>
                <SelectContent>
                  {getClients().map((client) => (
                    <SelectItem key={client.id} value={client.email}>
                      <div className="flex flex-col">
                        <span>{client.name}</span>
                        <span className="text-xs text-muted-foreground">{client.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Editor Selection (from Product Emails) */}
            <div className="space-y-2">
              <Label>{tx('Editor/Dueño del producto', 'Editor/Product owner')}</Label>
              <div className="relative">
                <Input
                  type="email"
                  list="editor-emails-list"
                  value={consultaForm.editor_email}
                  onChange={(e) => {
                    setConsultaForm({
                      ...consultaForm,
                      editor_id: '',
                      editor_name: e.target.value.split('@')[0],
                      editor_email: e.target.value,
                    });
                  }}
                  placeholder={tx('Buscar email del editor...', 'Search editor email...')}
                />
                <datalist id="editor-emails-list">
                  {(productEmails || []).slice(0, 500).map((email, idx) => (
                    <option key={idx} value={email} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-muted-foreground">
                {tx(
                  `${(productEmails || []).length} emails disponibles. Escribe para buscar.`,
                  `${(productEmails || []).length} emails available. Type to search.`
                )}
              </p>
            </div>

            {/* Worker Selection */}
            <div className="space-y-2">
              <Label>{tx('Trabajador *', 'Worker *')}</Label>
              <Select
                value={consultaForm.worker_email}
                onValueChange={(email) => {
                  const worker = getWorkers().find(w => w.email === email);
                  setConsultaForm({
                    ...consultaForm,
                    worker_id: worker?.id || '',
                    worker_name: worker?.name || '',
                    worker_email: email,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={tx('Seleccionar trabajador...', 'Select worker...')} />
                </SelectTrigger>
                <SelectContent>
                  {getWorkers().map((worker) => (
                    <SelectItem key={worker.id} value={worker.email}>
                      <div className="flex flex-col">
                        <span>{worker.name}</span>
                        <span className="text-xs text-muted-foreground">{worker.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>{tx('Fecha de Inicio', 'Start Date')}</Label>
              <Input
                type="date"
                value={consultaForm.start_date}
                onChange={(e) => setConsultaForm({ ...consultaForm, start_date: e.target.value })}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{tx('Descripción', 'Description')}</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder={tx('Describe la consulta...', 'Describe the query...')}
                value={consultaForm.description}
                onChange={(e) => setConsultaForm({ ...consultaForm, description: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{tx('Notas adicionales', 'Additional notes')}</Label>
              <textarea
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder={tx('Notas internas...', 'Internal notes...')}
                value={consultaForm.notes}
                onChange={(e) => setConsultaForm({ ...consultaForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsultaDialogOpen(false)}>
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button
              onClick={() => {
                if (editingConsulta) {
                  handleUpdateConsulta(editingConsulta.id, consultaForm);
                  setConsultaDialogOpen(false);
                } else {
                  handleCreateConsulta();
                }
              }}
              disabled={!consultaForm.client_email}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {editingConsulta 
                ? tx('Guardar Cambios', 'Save Changes')
                : tx('Crear Consulta', 'Create Query')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConsultasTab;
